"use client";
import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { useRouter } from "next/navigation";
import CheckIcon from "@mui/icons-material/Check";
import Image from "next/image";
import "../app/globals.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";
import LogoCarousel from "./LogoCarousel";
import AdsPageLogoCarousel from "./AdsPageLogoCarousel";

const HERO_BG =
  "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/HeroSection%203%20(1).webp";
const RIGHT_IMG =
  "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/Ad%20-%20Hero%20image.webp";

const HERO_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 700">
  <defs>
    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#07020F"/>
      <stop offset="45%" stop-color="#130A26"/>
      <stop offset="100%" stop-color="#1E0A3C"/>
    </linearGradient>
    <radialGradient id="glow1" cx="75%" cy="65%" r="38%">
      <stop offset="0%" stop-color="#6B0FA8" stop-opacity="0.75"/>
      <stop offset="60%" stop-color="#3A0568" stop-opacity="0.3"/>
      <stop offset="100%" stop-color="#07020F" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="glow2" cx="20%" cy="40%" r="25%">
      <stop offset="0%" stop-color="#2D0A5E" stop-opacity="0.45"/>
      <stop offset="100%" stop-color="#07020F" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="glow3" cx="85%" cy="25%" r="20%">
      <stop offset="0%" stop-color="#4A0880" stop-opacity="0.35"/>
      <stop offset="100%" stop-color="#07020F" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="1440" height="700" fill="url(#bgGrad)"/>
  <rect width="1440" height="700" fill="url(#glow1)"/>
  <rect width="1440" height="700" fill="url(#glow2)"/>
  <rect width="1440" height="700" fill="url(#glow3)"/>
  <path d="M0,700 L0,672 Q360,695 720,668 Q1080,642 1440,660 L1440,700 Z" fill="white"/>
</svg>`;

const trustItems = [
  "heroSectionAIPage.trust1",
  "heroSectionAIPage.trust2",
  "heroSectionAIPage.trust3",
  "heroSectionAIPage.trust4",
  "heroSectionAIPage.trust5",
];

const LANG_OPTIONS = [
  { value: "en", label: "English", countryCode: "us" },
  { value: "ar", label: "العربية", countryCode: "sa" },
];

// ── Custom Language Dropdown (Dark variant with fi-flags) ───
const LangDropdownDark = ({ currentLang, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected =
    LANG_OPTIONS.find((o) => o.value === currentLang) || LANG_OPTIONS[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Box ref={ref} sx={{ position: "relative", zIndex: 1300, flexShrink: 0 }}>
      {/* Trigger */}
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          height: { xs: "34px", sm: "36px", md: "38px" },
          padding: { xs: "0 8px", sm: "0 10px", md: "0 12px" },
          borderRadius: "8px",
          border: "1.5px solid",
          borderColor: open ? "#fff" : "rgba(255,255,255,0.4)",
          backgroundColor: open
            ? "rgba(255,255,255,0.15)"
            : "rgba(255,255,255,0.08)",
          cursor: "pointer",
          userSelect: "none",
          // ── flag-only on xs, label visible sm+ ──
          minWidth: { xs: "44px", sm: "130px", md: "148px" },
          transition: "all 0.2s ease",
          boxSizing: "border-box",
          "&:hover": {
            borderColor: "#fff",
            backgroundColor: "rgba(255,255,255,0.15)",
          },
        }}
      >
        {/* Flag — always visible */}
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <span
            className={`fi fi-${selected.countryCode}`}
            style={{
              width: "20px",
              height: "15px",
              borderRadius: "2px",
            }}
          />
        </Box>

        {/* Label — hidden on xs */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flex: 1,
            fontSize: { sm: "12px", md: "13px" },
            fontWeight: 500,
            color: "#fff",
            fontFamily: "var(--font-outfit)",
            whiteSpace: "nowrap",
            alignItems: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {selected.label}
        </Box>

        {/* Chevron */}
        <Box
          sx={{
            color: "rgba(255,255,255,0.7)",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </Box>
      </Box>

      {/* Dropdown */}
      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderRadius: "10px",
            border: "1.5px solid #F0F0F0",
            boxShadow: "0px 12px 32px rgba(0,0,0,0.2)",
            zIndex: 9999,
            overflow: "hidden",
            minWidth: "148px",
          }}
        >
          <Box
            sx={{ padding: "8px 14px 6px", borderBottom: "1px solid #F5F5F5" }}
          ></Box>
          {LANG_OPTIONS.map((option) => {
            const isActive = currentLang === option.value;
            return (
              <Box
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 14px",
                  cursor: "pointer",
                  backgroundColor: isActive ? "#FFFBF0" : "#fff",
                  borderLeft: isActive
                    ? "3px solid #FFB200"
                    : "3px solid transparent",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#FFFBF0" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <span
                    className={`fi fi-${option.countryCode}`}
                    style={{
                      width: "22px",
                      height: "16px",
                      borderRadius: "2px",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    fontSize: "14px",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? "#FFB200" : "#333",
                    fontFamily:
                      option.value === "ar"
                        ? "sans-serif"
                        : "var(--font-outfit)",
                  }}
                >
                  {option.label}
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      backgroundColor: "#FFB200",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M2 6L5 9L10 3"
                        stroke="white"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ── Main Component ───────────────────────────────────────────
const HeroMainSection = () => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [currentLang, setCurrentLang] = useState(i18n.language || "en");

  const handleLanguageChange = (newLang) => {
    setCurrentLang(newLang);
    i18n.changeLanguage(newLang);
    document.dir = newLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
  };

  return (
    <Box
      id="hero-main-section"
      sx={{
        backgroundImage: `url("${HERO_BG}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
        height: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        // ── tighter top padding on mobile so content isn't pushed down ──
        pt: { xs: 2, sm: 4, md: 1 },
        pb: { xs: 4, sm: 6, md: 6 },
        direction: isArabic ? "rtl" : "ltr",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── Main Content ── */}
      <Container
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 3, md: 5, lg: 2 },
          // ── reduce top margin on mobile ──
          mt: { xs: "25%", sm: "18%", md: "10%" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            // ── on xs: text first, then image below ──
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "center" },
            justifyContent: "space-between",
            gap: { xs: 2, sm: 3, md: 2 },
          }}
        >
          {/* ── LEFT CONTENT ── */}
          <Box
            sx={{
              flex: "0 0 48%",
              maxWidth: { xs: "100%", md: "48%" },
              display: "flex",
              flexDirection: "column",
              alignItems: {
                xs: "flex-start",
                md: isArabic ? "flex-start" : "flex-start",
              },
              textAlign: { xs: "center", md: isArabic ? "right" : "left" },
            }}
          >
            {/* Badge */}
            <Box
              sx={{
                border: "1px solid #FFF",
                borderRadius: "50px",
                px: { xs: 1.5, sm: 2.3 },
                py: { xs: 0.4, sm: 1.5 },
                mb: { xs: 2, md: 3.5 },
                display: "inline-flex",
                maxWidth: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.62rem", sm: "0.82rem", md: "0.88rem" },
                  color: "#DAB2FF",
                  fontWeight: 400,
                  letterSpacing: "0.02em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {t("heroSectionAIPage.badge")}
              </Typography>
            </Box>

            {/* Heading */}
            <Typography
              component="h1"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 800,
                fontSize: {
                  xs: "1.75rem",
                  sm: "2.75rem",
                  md: "3rem",
                  lg: "3.4rem",
                },
                lineHeight: { xs: 1.5, md: 1.18 },
                color: "#fff",
                mb: { xs: 1.5, md: 3 },
                letterSpacing: "-0.02em",
                textAlign: {
                  xs: isArabic ? "right" : "left",
                  md: isArabic ? "right" : "left",
                },
                // ── word wrap ensure karo ──
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {t("heroSectionAIPage.heading1")}{" "}
              <span style={{ color: "#FF4B54" }}>
                {t("heroSectionAIPage.headingHighlight")}
              </span>{" "}
              {/* xs par <br/> hide karo */}
              <Box
                component="br"
                sx={{ display: { xs: "none", md: "block" } }}
              />
              {t("heroSectionAIPage.heading2")}
              <Box
                component="br"
                sx={{ display: { xs: "none", md: "block" } }}
              />
              {t("heroSectionAIPage.heading3")}
            </Typography>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: {
                  xs: "0.82rem",
                  sm: "0.92rem",
                  md: "0.97rem",
                  lg: "1.1rem",
                },
                color: "#D1D5DC",
                lineHeight: 1.8,
                mb: { xs: 2.5, md: 5 },
                maxWidth: { xs: "100%", sm: "90%", md: "92%" },
                textAlign: {
                  xs: isArabic ? "right" : "left",
                  md: isArabic ? "right" : "left",
                },
                alignSelf: "stretch",
              }}
            >
              {t("heroSectionAIPage.description")}
            </Typography>

            {/* CTA Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1.5, sm: 2 },
                alignItems: {
                  xs: "stretch",
                  md: isArabic ? "flex-end" : "flex-start",
                },
                mb: { xs: 3, md: 4.5 },
                width: { xs: "auto", sm: "auto" },
              }}
            >
              {/* Primary Button */}
              <Button
                id="btn-two"
                // onClick={() => {
                //   const el = document.getElementById("book-demo-section");
                //   if (el) el.scrollIntoView({ behavior: "smooth" });
                // }}
                 onClick={() => {
  window.dispatchEvent(new Event("open-book-demo-modal"));
}}
                endIcon={
                  <ArrowForwardIcon
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      color: "#fff",
                      transform: isArabic ? "rotate(180deg)" : "none",
                      mr: 1.3,
                      mt: isArabic ? "5px" : "0.4px",
                    }}
                  />
                }
                sx={{
                  background: "#FFA717",
                  color: "#fff",
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 500,
                  fontSize: { xs: "0.82rem", sm: "0.93rem", md: "0.97rem" },
                  px: { xs: 2.5, sm: 3.5 },
                  py: { xs: 1.3, sm: 2 },
                  borderRadius: "50px",
                  textTransform: "none",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  width: { xs: "100%", sm: "auto" },
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    background: "#FFA717",
                    transform: "scale(1.05)",
                  },
                  "&:active": { transform: "translateY(0)" },
                }}
              >
                {t("heroSectionAIPage.primaryCta")}
              </Button>
            </Box>
          </Box>

          {/* ── RIGHT IMAGE ── */}
          <Box
            sx={{
              flex: "0 0 50%",
              maxWidth: { xs: "100%", md: "50%" },
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "center", md: "flex-end" },
              alignSelf: "center",
              position: "relative",
              pl: { md: isArabic ? 0 : 1 },
              pr: { md: isArabic ? 1 : 0 },
              // ── tighter negative margin on xs to reduce gap ──
              mt: { xs: -2, sm: -1, md: 0 },
            }}
          >
            <Box
              component="img"
              src={RIGHT_IMG}
              alt="AI Recruiting Agents"
              sx={{
                width: "100%",
                // ── smaller image on xs so it doesn't dominate ──
                maxWidth: { xs: 300, sm: 380, md: "100%" },
                height: "auto",
                display: "block",
                objectFit: "contain",
                filter: "drop-shadow(0 0 40px rgba(139,92,246,0.15))",
              }}
            />
          </Box>
        </Box>

        {/* ── Trust Bar ── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 0.8, sm: 1.5, md: 2.5 },
            justifyContent: "center",
            mt: { xs: "1.2%", md: "4%" },
            px: { xs: 1, sm: 0 },
          }}
        >
          {trustItems.map((key) => (
            <Box
              key={key}
              sx={{ display: "flex", alignItems: "center", gap: 0.6 }}
            >
              <CheckIcon
                sx={{
                  fontSize: { xs: "0.8rem", sm: "0.9rem" },
                  color: "#05DF72",
                  flexShrink: 0,
                  mr: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.72rem", sm: "0.8rem", md: "1rem" },
                  color: "#D1D5DC",
                  whiteSpace: "nowrap",
                }}
              >
                {t(key)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mb: {
              xs: "12%",
              sm: "12%",
              md: "5%",
            },
          }}
        >
          <AdsPageLogoCarousel />
        </Box>
      </Container>
    </Box>
  );
};

export default HeroMainSection;
